import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import {
	createPortfolioImage,
	deletePortfolioImage,
	getPortfolioImages,
} from './portfolioImagesSlice'
import { createPortfolioNote, getPortfolioNotes } from './portfolioNoteSlice'

const initialState = {
	entities: {},
	ids: [],
	recents: [],
	isLoading: false,
	isInitialized: false,
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

export const initializePortfolios = createAsyncThunk(
	'portfolios/initialize',
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/portfolios/sharedportfolios`, prepConfig(getState))
			console.log('✅ Successfully fetched portfolios:', data?.length || 0, 'items')
			return data
		} catch (error: any) {
			console.error('❌ Failed to initialize portfolios:', {
				message: error?.message,
				response: error?.response?.data,
				status: error?.response?.status,
			})
			return rejectWithValue(error?.response?.data?.message || 'Failed to initialize portfolios')
		}
	},
)

export const initializeAdminPortfolios = createAsyncThunk(
	'portfolios/initializeAdmin',
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/portfolios/admin`, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to initialize admin portfolios:', error)
			return rejectWithValue('Failed to initialize portfolios')
		}
	},
)

export const initializeArchivedPortfolios = createAsyncThunk(
	'portfolios/initializeArchived',
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/portfolios/archived', prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to initialize portfolios')
		}
	},
)

export const createPortfolio = createAsyncThunk(
	'portfolios/create',
	async (portfolio, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(`/api/portfolios`, portfolio, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to create portfolio:', error)
			return rejectWithValue('Failed to create portfolio')
		}
	},
)

export const deletePortfolio = createAsyncThunk(
	'portfolios/delete',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId } = payload

		try {
			const { data } = await axios.put(`/api/portfolios/${portfolioId}/delete`, {}, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete portfolio')
		}
	},
)

export const patchPortfolio = createAsyncThunk(
	'portfolios/patch',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, updates } = payload

		try {
			const { data } = await axios.patch(
				`/api/portfolios/${portfolioId}/patch`,
				updates,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			console.error(error)
			return rejectWithValue('Unable to patch portfolio')
		}
	},
)

export const updatePortfolio = createAsyncThunk(
	'portfolios/update',
	async (payload: any, { getState, rejectWithValue }) => {
		console.log(`payload: `, payload)
		try {
			const { data } = await axios.put(`/api/portfolios/${payload._id}`, payload, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to update portfolio:', error)
			return rejectWithValue('Failed to update portfolio')
		}
	},
)

export const addLocationToPortfolio = createAsyncThunk(
	'portfolios/addLocation',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, locationId } = payload

		try {
			const { data } = await axios.post(
				`/api/portfolios/${portfolioId}/locations`,
				{ locationId },
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to add location')
		}
	},
)

export const removeLocationFromPortfolio = createAsyncThunk(
	'portfolios/removeLocationFromPortfolio',
	async (payload: any, { getState, rejectWithValue }) => {
		const { locationId, portfolioId } = payload

		try {
			const { data } = await axios.delete(
				`/api/portfolios/${portfolioId}/locations/${locationId}`,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to add partner')
		}
	},
)

/**
 * Locations + projects in a portfolio, flagged with whether a given user is
 * already a partner on each. Drives the per-asset access chooser.
 *
 * Deliberately not written into `entities` — it is ephemeral view data that
 * belongs to the dialog, not to the portfolio store.
 */
export const fetchPortfolioPartnerScope = createAsyncThunk(
	'portfolios/partnerScope',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, partnerUserId } = payload

		try {
			const { data } = await axios.get(
				`/api/portfolios/${portfolioId}/partner-scope?partnerUserId=${partnerUserId}`,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to load portfolio locations and projects')
		}
	},
)

export const addPartnerToPortfolio = createAsyncThunk(
	'portfolios/addPartner',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, partner, portfolioAccessLevel = 'full', grants = [] } = payload

		try {
			const { data } = await axios.post(
				`/api/portfolios/${portfolioId}/addpartners`,
				{
					partnerId: partner._id,
					// `partner.partner` is the populated User; the API wants its id.
					// Sending the whole object here used to rely on Mongoose casting.
					partnerUserId: partner.partner?._id ?? partner.partner,
					portfolioAccessLevel,
					grants,
				},
				prepConfig(getState),
			)
			return data
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to add partner',
			)
		}
	},
)

export const addUpdateNoteToPortfolio = createAsyncThunk(
	'portfolios/addUpdateNote',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, updateNote } = payload

		try {
			const { data } = await axios.post(
				`/api/portfolios/${portfolioId}/updateNote`,
				{ updateNote, portfolioId },
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to add update note')
		}
	},
)

export const removePartnerFromPortfolio = createAsyncThunk(
	'portfolios/removePartner',
	async (payload: any, { getState, rejectWithValue }) => {
		const { portfolioId, partnerUserId } = payload

		try {
			const { data } = await axios.post(
				`/api/portfolios/${portfolioId}/removePartner`,
				// The API historically called this `partnerId` while always
				// carrying a User id; it accepts both keys during the changeover.
				{ partnerUserId },
				prepConfig(getState),
			)
			return data
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to remove partner',
			)
		}
	},
)

// The argument type is stated explicitly. Without it, createAsyncThunk infers
// the untyped `portfolioId` parameter as `void`, so callers that correctly pass
// an id fail to compile while `getUpdatedPortfolio()` — which produced a request
// to /api/portfolios/undefined — type-checked fine. The typing was hiding the bug.
export const getUpdatedPortfolio = createAsyncThunk<any, string>(
	'portfolios/getUpdate',
	async (portfolioId, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/portfolios/${portfolioId}`, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to update portfolio')
		}
	},
)

// Extract the key out of the entitiy object
// eslint-disable-next-line no-unused-vars
const removeKey = (key, { [key]: _, ...rest }) => rest

const portfolioSlice = createSlice({
	name: 'portfolios',
	initialState,
	reducers: {},
	extraReducers: {
		[initializePortfolios.fulfilled as any]: (state, action) => {
			const portfolios = action.payload

			// Filter out invalid entries and sort by updatedAt in reverse chronological order (most recent first)
			const validPortfolios = (portfolios || []).filter(p => p && p._id)
			const sortedPortfolios = [...validPortfolios].sort((a, b) => {
				const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
				const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
				return dateB - dateA
			})

			state.recents = []
			state.ids = sortedPortfolios.map((loc, index) => {
				state.entities[loc._id] = loc

				if (index < 4) {
					state.recents.push(loc._id)
				}
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
			state.error = null
		},
		[initializePortfolios.pending as any]: state => {
			state.isLoading = true
		},
		[initializePortfolios.rejected as any]: (state, action) => {
			console.error('🔴 Portfolio initialization rejected:', action.error, action.payload)
			state.isLoading = false
			state.isInitialized = true
			state.error = action.payload || action.error.message
		},

		[initializeAdminPortfolios.fulfilled as any]: (state, action) => {
			const portfolios = action.payload

			state.ids = portfolios?.map((loc, index) => {
				state.entities[loc._id] = loc
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeAdminPortfolios.pending as any]: state => {
			state.isLoading = true
		},
		[initializeAdminPortfolios.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},

		[initializeArchivedPortfolios.fulfilled as any]: (state, action) => {
			const portfolios = action.payload

			state.ids = portfolios?.map((loc, index) => {
				state.entities[loc._id] = loc
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeArchivedPortfolios.pending as any]: state => {
			state.isLoading = true
		},
		[initializeArchivedPortfolios.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},

		[createPortfolio.fulfilled as any]: (state, action) => {
			const portfolio = action.payload
			state.entities[portfolio._id] = portfolio
			state.ids.push(portfolio._id)
			state.recents.unshift(portfolio._id)

			if (state.recents.length > 4) {
				state.recents.pop()
			}
		},
		[deletePortfolio.fulfilled as any]: (state, action) => {
			const id = action.meta.arg
			state.entities = removeKey(id, state.entities)
			state.ids = state.ids.filter(stateId => stateId !== id)
		},
		[patchPortfolio.fulfilled as any]: (state, action) => {
			const portfolio = action.payload
			state.entities[portfolio._id] = { ...action.payload, ...state.entities[portfolio._id] }
			state.isInitialized = false
		},

		[updatePortfolio.fulfilled as any]: (state, action) => {
			const portfolio = action.payload
			const portfolioId = portfolio._id
			state.entities[portfolioId] = portfolio
		},
		[getUpdatedPortfolio.fulfilled as any]: (state, action) => {
			// works for any asset type since all types are stored under entities
			state.entities[action.meta.arg] = { ...action.payload, ...state.entities[action.meta.arg] }
			state.isLoading = false
		},
		[getUpdatedPortfolio.rejected as any]: (state, action) => {
			state.error = action.error.message
		},
		[addLocationToPortfolio.fulfilled as any]: (state, action) => {
			const locations = action.payload
			state.entities[action.meta.arg.portfolioId].locations = locations
			state.isInitialized = false
		},
		[addLocationToPortfolio.rejected as any]: (state, action) => {
			state.error = 'Something went wrong'
		},
		[addPartnerToPortfolio.fulfilled as any]: (state, action) => {
			// The endpoint returns the fully populated portfolio now, so the
			// entity can be replaced outright instead of patching in a bare
			// partners array whose ids were never populated.
			const portfolio = action.payload
			state.entities[portfolio._id] = portfolio
			// The cascade also mutated locations and projects, so anything
			// derived from them needs refetching.
			state.isInitialized = false
		},
		[addPartnerToPortfolio.rejected as any]: (state, action) => {
			state.error = action.payload || 'Failed to add partner'
		},
		[removeLocationFromPortfolio.fulfilled as any]: (state, action) => {
			const locations = action.payload
			state.entities[action.meta.arg.portfolioId].locations = locations
			state.isInitialized = false
		},
		[getPortfolioNotes.fulfilled as any]: (state, action) => {
			const notes = action.payload
			const portfolioId = action.meta.arg
			const portfolioRef = state.entities[portfolioId]
			if (portfolioRef && notes?.length) {
				const portfolioNoteIds = notes.map(n => n._id)
				portfolioRef.notes = portfolioNoteIds
			}
		},
		[createPortfolioNote.fulfilled as any]: (state, action) => {
			const newNote = action.payload
			const existingNotes = state.entities[newNote.portfolioid].notes
			if (existingNotes) {
				existingNotes.unshift(newNote._id)
			} else {
				state.entities[newNote.portfolioid].notes = [newNote._id]
			}
		},
		[getPortfolioImages.fulfilled as any]: (state, action) => {
			const images = action.payload
			const locationId = action.meta.arg
			const locationRef = state.entities[locationId]
			if (locationRef && images?.length) {
				const locationImageIds = images.map(n => n._id)
				locationRef.images = locationImageIds
			}
		},
		[createPortfolioImage.fulfilled as any]: (state, action) => {
			const image = action.payload
			const locationId = image.portfolioid
			const locationRef = state.entities[locationId]
			if (locationRef && image) {
				if (locationRef.images) {
					locationRef.images.push(image._id)
				} else {
					locationRef.images = [image._id]
				}
			}
		},
		[deletePortfolioImage.fulfilled as any]: (state, action) => {
			const locationId = action.payload.portfolioid
			const imageId = action.meta.arg
			const updatedImageIds = state.entities[locationId]?.images?.filter((imgId: any) => {
				return imgId !== imageId
			})
			state.entities[locationId].images = updatedImageIds
		},
		[removePartnerFromPortfolio.fulfilled as any]: (state, action) => {
			// Also returns the populated portfolio; the revoke touched child
			// locations and projects too, so force a refetch.
			const portfolio = action.payload
			state.entities[portfolio._id] = portfolio
			state.isInitialized = false
		},
		[removePartnerFromPortfolio.rejected as any]: (state, action) => {
			state.error = action.payload || 'Failed to remove partner'
		},
		[addUpdateNoteToPortfolio.fulfilled as any]: (state, action) => {
			// This endpoint returns updateHistory, not a portfolio.
			const portfolioId = action.meta.arg.portfolioId
			if (state.entities[portfolioId]) {
				state.entities[portfolioId].updateHistory = action.payload
			}
		},
		[addUpdateNoteToPortfolio.rejected as any]: (state, action) => {
			console.log(action)
		},
	} as any,
})

export default portfolioSlice
